import { ConditionValue, InputSheet, InputValues, SheetInput, SweepCellChange, SweepCellFields, SweepInput, findInput, setSweepCell } from '@/reports/inputs';

/**
 * What was typed on a record and is not saved yet, on the sheet (schema
 * version) it was typed on. An input is changed whole — anything but a sweep,
 * or a sweep changed whole — or, for a sweep, cell input by cell input; a save
 * carries only those cell inputs, so it never sends cells someone else saved.
 */
export interface RecordDraft {
  sheet: InputSheet;
  schemaVersionId: string;
  /** inputs changed whole, by id */
  whole: InputValues;
  /** sweeps changed cell by cell: by sweep, each cell input set (`editKey`) */
  cells: Record<string, Record<string, SweepCellChange>>;
}

/** The sheet a record is entered on, as its reports here were last fetched. */
export interface RecordSheet {
  sheet: InputSheet;
  schemaVersionId: string;
}

/** One change typed on a fill page: an input's whole value, or for a sweep, one cell input with the sweep it makes. */
export interface InputEdit {
  inputId: string;
  value: unknown;
  cell?: SweepCellChange;
}

/** A sweep cell, by its conditions' values in any order: the same cell on a sheet that orders them otherwise. */
const cellId = (at: Record<string, ConditionValue>) => JSON.stringify(Object.keys(at).sort().map((k) => [k, String(at[k])]));

/** A sweep's cell input: its cell and which input. */
const editKey = (cell: SweepCellChange) => JSON.stringify([cellId(cell.at), cell.field]);

const isEmpty = (draft: RecordDraft) => Object.keys(draft.whole).length === 0 && Object.keys(draft.cells).length === 0;

/** The draft, or null when it holds nothing. */
export const some = (draft: RecordDraft | null): RecordDraft | null => (draft && !isEmpty(draft) ? draft : null);

/** The inputs a draft changes. */
export const draftInputs = (draft: RecordDraft | null): string[] => (draft ? [...Object.keys(draft.whole), ...Object.keys(draft.cells)] : []);

/** A sweep's value with cell inputs set over it. */
const applyCells = (sweep: SweepInput, value: unknown, edits: Record<string, SweepCellChange> | undefined) =>
  Object.values(edits ?? {}).reduce<unknown>((v, c) => setSweepCell(sweep, v, c.at, c.field, c.value), value);

/** A draft with one more change. A cell input set on a sweep the draft changes whole goes into that whole value (the page's copy, which has it). */
export function addEdit(draft: RecordDraft | null, on: RecordSheet, edit: InputEdit): RecordDraft {
  const d = draft ?? { ...on, whole: {}, cells: {} };
  const { inputId, value, cell } = edit;
  if (cell && !(inputId in d.whole)) return { ...d, cells: { ...d.cells, [inputId]: { ...d.cells[inputId], [editKey(cell)]: cell } } };
  const { [inputId]: _replaced, ...cells } = d.cells;
  return { ...d, whole: { ...d.whole, [inputId]: value }, cells };
}

/** Two drafts on one sheet as one, the later's changes over the earlier's. */
export function mergeDrafts(earlier: RecordDraft | null, later: RecordDraft | null): RecordDraft | null {
  if (!earlier || !later) return some(earlier ?? later);
  const whole = { ...earlier.whole, ...later.whole };
  const cells: RecordDraft['cells'] = {};
  for (const inputId of new Set([...Object.keys(earlier.cells), ...Object.keys(later.cells)])) {
    if (inputId in later.whole) continue;
    const sweep = findInput(later.sheet, inputId);
    // changed whole earlier, cell by cell since: still whole, with those cells set
    if (inputId in earlier.whole && sweep?.kind === 'sweep') whole[inputId] = applyCells(sweep, earlier.whole[inputId], later.cells[inputId]);
    else cells[inputId] = { ...earlier.cells[inputId], ...later.cells[inputId] };
  }
  return some({ ...later, whole, cells });
}

/** What a table's rows or a sweep's cells hold; null for any other input. */
const fieldsOf = (input: SheetInput) => (input.kind === 'table' ? input.columns.map((c) => c.id).join() : input.kind === 'sweep' ? input.record.map((r) => r.id).join() : null);

/**
 * A draft typed on a sheet the record has since moved up from, brought onto
 * the record's sheet (`draft`). Cell inputs need nothing: a save names each
 * cell by its conditions' values. A table, or a sweep changed whole, that
 * holds other columns or cell inputs on the new sheet is `held` back on the
 * old one: saved whole it would wipe what only the new version has.
 */
export function rebaseDraft(draft: RecordDraft | null, on: RecordSheet): { draft: RecordDraft | null; held: RecordDraft | null } {
  if (!draft || draft.schemaVersionId === on.schemaVersionId) return { draft, held: null };
  const moved: RecordDraft = { ...on, whole: {}, cells: draft.cells };
  const held: RecordDraft = { ...draft, whole: {}, cells: {} };
  for (const [inputId, value] of Object.entries(draft.whole)) {
    const before = findInput(draft.sheet, inputId);
    const after = findInput(on.sheet, inputId);
    (before && after && fieldsOf(before) !== fieldsOf(after) ? held : moved).whole[inputId] = value;
  }
  return { draft: some(moved), held: some(held) };
}

/** What the record shows with the drafts over what is stored, earliest first; cell inputs are set on `sheet`'s sweeps. */
export function overlay(sheet: InputSheet, stored: InputValues, drafts: Array<RecordDraft | null>): InputValues {
  let next = stored;
  for (const draft of drafts) {
    if (!draft) continue;
    next = { ...next, ...draft.whole };
    for (const [inputId, edits] of Object.entries(draft.cells)) {
      const sweep = findInput(sheet, inputId);
      if (sweep?.kind === 'sweep') next[inputId] = applyCells(sweep, next[inputId], edits);
    }
  }
  return next;
}

/**
 * What a save sends: the inputs changed whole as they are, and each sweep
 * changed cell by cell made of only the cell inputs the draft carries, named
 * in `cells` — so the two always match.
 */
export function draftBody(draft: RecordDraft): { values: InputValues; cells: Record<string, SweepCellFields[]> } {
  const values: InputValues = { ...draft.whole };
  const cells: Record<string, SweepCellFields[]> = {};
  for (const [inputId, edits] of Object.entries(draft.cells)) {
    const byCell = new Map<string, { at: Record<string, ConditionValue>; values: Record<string, unknown>; fields: string[] }>();
    for (const edit of Object.values(edits)) {
      const cell = byCell.get(cellId(edit.at)) ?? { at: edit.at, values: {}, fields: [] };
      cell.values[edit.field] = edit.value;
      if (!cell.fields.includes(edit.field)) cell.fields.push(edit.field);
      byCell.set(cellId(edit.at), cell);
    }
    values[inputId] = { cells: [...byCell.values()].map((c) => ({ at: c.at, values: c.values })) };
    cells[inputId] = [...byCell.values()].map((c) => ({ at: c.at, fields: c.fields }));
  }
  return { values, cells };
}
